import { useEffect, useReducer } from "react";
import { doc, onSnapshot, type DocumentSnapshot, type FirestoreError } from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import {
  isMovementState,
  MOVEMENT_STATE_DOC_ID,
  type MovementState,
} from "@lemonadetraining/contracts";

export type MovementStateStatus = "idle" | "loading" | "ready" | "empty" | "error";

export interface UseMovementStateResult {
  status: MovementStateStatus;
  movements: ReadonlyMap<string, MovementState>;
  error: Error | null;
}

type Action =
  | { type: "reset" }
  | { type: "loading"; uid: string }
  | { type: "ready"; uid: string; movements: ReadonlyMap<string, MovementState> }
  | { type: "empty"; uid: string }
  | { type: "error"; uid: string; error: Error };

const NONE: ReadonlyMap<string, MovementState> = new Map();

const IDLE: UseMovementStateResult = { status: "idle", movements: NONE, error: null };
const LOADING: UseMovementStateResult = { status: "loading", movements: NONE, error: null };

// The uid the result belongs to, so another user's numbers are never returned.
interface OwnedState {
  owner: string | null;
  result: UseMovementStateResult;
}

const initialState: OwnedState = { owner: null, result: IDLE };

function reducer(state: OwnedState, action: Action): OwnedState {
  switch (action.type) {
    case "reset":
      return state.owner === null ? state : initialState;
    case "loading":
      return { owner: action.uid, result: LOADING };
    case "ready":
      return {
        owner: action.uid,
        result: { status: "ready", movements: action.movements, error: null },
      };
    case "empty":
      return state.owner === action.uid && state.result.status === "empty"
        ? state
        : { owner: action.uid, result: { status: "empty", movements: NONE, error: null } };
    case "error":
      return {
        owner: action.uid,
        result: { status: "error", movements: NONE, error: action.error },
      };
  }
}

function parseMovements(raw: unknown): Map<string, MovementState> | null {
  if (raw === undefined || raw === null) return new Map();
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const movements = new Map<string, MovementState>();
  for (const [id, state] of Object.entries(raw)) {
    if (!isMovementState(state)) return null;
    movements.set(id, state);
  }
  return movements;
}

/**
 * Live `Users/{uid}/AthleteProfile/movementState`. A missing document is the
 * normal state of an athlete who has logged nothing; an entry that fails
 * validation fails the whole read rather than showing partial numbers.
 */
export function useMovementState(
  uid: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
): UseMovementStateResult {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!uid || !enabled) {
      dispatch({ type: "reset" });
      return;
    }
    dispatch({ type: "loading", uid });

    const ref = doc(firebaseDB, "Users", uid, "AthleteProfile", MOVEMENT_STATE_DOC_ID);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot: DocumentSnapshot) => {
        if (!snapshot.exists()) {
          dispatch({ type: "empty", uid });
          return;
        }
        const movements = parseMovements(snapshot.data().movements);
        if (!movements) {
          dispatch({ type: "error", uid, error: new Error("Invalid movement state document") });
          return;
        }
        dispatch({ type: "ready", uid, movements });
      },
      (err: FirestoreError) => dispatch({ type: "error", uid, error: err })
    );
    return () => unsubscribe();
  }, [uid, enabled]);

  if (!uid || !enabled) return IDLE;
  return state.owner === uid ? state.result : LOADING;
}
